import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo
} from "react";
import PropTypes from "prop-types";
import Toasts from "../components/toast-notification";

export const ToastContext = createContext({
  showToast: () => {},
  hideToast: () => {}
});

export function ToastNotifications({ children, components }) {
  // for each toast to have unique id
  const newIdRef = useRef(1);
  const timeoutsRef = useRef([]);
  const [toasts, setToasts] = useState({ ids: new Set(), byId: {} });
  const hideToast = useCallback(idToDelete => {
    setToasts(allToasts => {
      const newIds = new Set(allToasts.ids);
      newIds.delete(idToDelete);
      return {
        ids: newIds,
        byId: { ...allToasts.byId, [idToDelete]: null }
      };
    });
  }, []);
  /**
   * external id can be given, to more granular control over closing.
   */
  const showToast = useCallback(
    ({ title, message, closeAfter = 2000, id = newIdRef.current }) => {
      setToasts(allToasts => ({
        ids: new Set(allToasts.ids).add(id),
        byId: { ...allToasts.byId, [id]: { id, message, title, closeAfter } }
      }));
      if (closeAfter !== 0) {
        // 0 is for persistent toasts, that user has to close manually
        timeoutsRef.current = [
          ...timeoutsRef.current,
          setTimeout(() => {
            hideToast(id);
          }, closeAfter)
        ];
      }
      newIdRef.current += 1;
    },
    [newIdRef.current, hideToast]
  );

  const context = useMemo(
    () => ({ showToast, hideToast }),
    [showToast, hideToast]
  );

  // clear all the timeouts
  useEffect(() => () => timeoutsRef.current.forEach(t => clearTimeout(t)), []);

  return (
    <ToastContext.Provider value={context}>
      {children}
      <Toasts
        toasts={[...toasts.ids].map(id => toasts.byId[id])}
        onClose={hideToast}
        components={components}
      />
    </ToastContext.Provider>
  );
}

ToastNotifications.propTypes = {
  children: PropTypes.node.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  components: PropTypes.object.isRequired
};
